import * as Sentry from '@sentry/browser'
import {
    BeginnerHintFormat,
    BuildGameConfigurations,
    GameEndData,
    SelectDiceFormat,
    SelectPlayerFormat,
} from '@colonist/game-base-types'
import {
    BankTransactionLedger,
    BeginnerModeActionState,
    GameDataFirstGameState,
    GameUpdate,
    GameUpdateData,
    SocketGameStateUpdate,
    SpectatorData,
    VoteState,
} from '@colonist/game-state-types'
import {GameClientState} from '../../../../shared/GameClientState'
import {Sound} from '../Sound'
import {CA} from '../analytics/Analytics'
import {SocketActionController} from './SocketActionController'
import {TranslationObject} from '@colonist/i18n'
import {
    CardExchangeFormat,
    CardPopupInformation,
    SelectCardPopupForPendingDistributionFormat,
    SelectCardPopupStealCardFromAnotherPlayerFormat,
    SelectCardPopupUsingDevelopmentCardFormat,
    SelectCardsToGiveToAnotherPlayer,
    SelectCardToDiscardFormat,
    SelectPlayerAndResourceCardFormat,
    SelectPlayerUsingDevelopmentCardFormat,
} from '@colonist/game-card-types'
import {ChatData} from '@colonist/shared-types'
import {ViewPopupTemporary} from '../ui_popup/ViewPopupTemporary'
import {notificationController, socket, uiGameManager} from '../EntryShared'
import {UITopNotificationType} from '../notifications/UINotificationController'
import {MapSettingEnum, ModeSettingEnum, SOCKET_CLOSE_GAME_CLIENT_ERROR} from '@colonist/types'
import {createDeferredPromise, DeferredPromise, SequencedDataReceiver, TimeUtils} from '@colonist/utils'
import {StorageWrapper} from '../StorageWrapper'
import {UIGameManager} from '../ui_game/UIGameManager'
import {CardEnum, GameChatMessageData} from '@colonist/game-states'
import {GameStateEnums, NetworkEnums} from '../../../../shared/GameEnums'
import {ReconnectFunction, UIReconnectionController} from '../ui_reconnection/UIReconnectionController'
import type {SocketController} from './SocketController'
import {addGlobalSpectatorMessage, setGlobalCanResign, setGlobalSpectators} from '@colonist/ui-game'
import {SocketLobbySend} from './SocketLobbySend.ts'
import {AnalyticEventCategory, AnalyticEventType} from '../../../../shared/entity_datas/EntityDatas.ts'
import {omit, omitBy} from 'lodash-es'

const GAME_STARTED_DELAY_FOR_NATURAL_EFFECT = 500

export class SocketGameListen {
    readonly gameState: GameClientState
    databaseGameId: string
    private gameSettingId: string | undefined
    readonly socketActionController: SocketActionController
    private readonly uiGameManager: UIGameManager
    // eslint-disable-next-line func-call-spacing
    private readonly gameStateHandlers = new Map<GameUpdate, (data: any) => void>()
    private readonly gameStateReceiver: SequencedDataReceiver<GameUpdateData>

    private playedStartGameSound: boolean = false

    private readonly gameClientRestorationController: UIReconnectionController

    private currentReconnectAttempt: DeferredPromise<'connected' | 'retry'> | undefined

    constructor(socketController: SocketController, socketActionController: SocketActionController) {
        this.gameState = new GameClientState()
        this.socketActionController = socketActionController
        this.uiGameManager = uiGameManager

        const onGameReconnectionAttempt: ReconnectFunction = () => {
            const reconnectPromise = createDeferredPromise<'connected' | 'retry'>()
            this.currentReconnectAttempt = reconnectPromise

            setTimeout(() => {
                if(!reconnectPromise.pending) return

                reconnectPromise.resolve('retry')
            }, 10 * TimeUtils.MILLISECONDS)

            this.socketActionController.uiGameManager?.socketGameSend.requestGameState()

            return reconnectPromise
        }
        this.gameClientRestorationController = new UIReconnectionController(onGameReconnectionAttempt, () => true)

        this.gameStateReceiver = new SequencedDataReceiver((data) => {
            try {
                this.gameStateHandlers.get(data.type)?.(data.payload)
            } catch(e) {
                // eslint-disable-next-line no-console
                console.log(e)
                socketController.close(SOCKET_CLOSE_GAME_CLIENT_ERROR)
                Sentry.captureException(e)
            }
        }, (lastSequence, now, errorReason, pendingData, lastReleasedData) => {
            void CA.sendAnalyticEvent({
                category: AnalyticEventCategory.Socket,
                type: AnalyticEventType.SocketSequenceError,
                dimensions: {
                    databaseGameId: this.databaseGameId,
                    lastSequence: lastSequence,
                    time: now,
                    reason: errorReason,
                    pendingData: pendingData.map((entry) => ({t: entry.added, s: entry.data.sequence, gu: entry.data.type})),
                    lastReleasedData: lastReleasedData.map((entry) => ({t: entry.added, s: entry.data.sequence, gu: entry.data.type})),
                    monitorStats: omitBy(omit(socket.monitor.stats, ['buckets']), (value) => value == 0),
                },
            })

            this.restoreGameClientWithoutClosingSocket()
        })

        socketController.setStateHandler(NetworkEnums.State.GameStateUpdate, (data: GameUpdateData) => {
            this.gameStateReceiver.addData(data)
        })

        socketController.registerSessionEstablishedObserver(() => {
            this.reset()

            if(this.gameSettingId == undefined) return
            SocketLobbySend.reconnectGame(this.gameSettingId, CA.Label.AutoRejoinGame)
            this.gameClientRestorationController.startReconnecting()
        })

        this.gameStateHandlers.set(GameUpdate.FirstGameState, (data: GameDataFirstGameState) => {
            if(this.socketActionController.uiGameManager == undefined) return

            socket.monitor.activate()

            // ======================
            // Order maters, these variables have to be set before executing other logic
            this.gameState.serverId = data.serverId
            this.databaseGameId = data.databaseGameId
            this.gameSettingId = data.gameSettingId
            // ======================

            if(data.shouldResetGameClient) {
                this.currentReconnectAttempt?.resolve('connected')
                this.socketActionController.uiGameManager.resetGameUIToRebuildOnReconnect()
            }

            notificationController?.removeDisconnectBanners()

            this.socketActionController.onConnectionEstablished(data)
            if(data.isReconnectingSession) this.playedStartGameSound = true
        })

        this.gameStateHandlers.set(GameUpdate.SpectatorData, (data: SpectatorData[]) => {
            setGlobalSpectators(data)
        })

        this.gameStateHandlers.set(GameUpdate.BeginnerHintActivated, (data: BeginnerHintFormat) => {
            this.socketActionController.onBeginnerHintActivated(data)
            this.uiGameManager?.gameStore.getState().gameBeginnerHint.setBeginnerHintData(data)
        })

        this.gameStateHandlers.set(GameUpdate.BeginnerModeActionUpdate, (data: BeginnerModeActionState) => {
            this.socketActionController.onBeginnerModeActionUpdate(data)
            this.uiGameManager?.gameStore.getState().gameBeginnerHint.setBeginnerActionState(data)
        })

        this.gameStateHandlers.set(GameUpdate.BeginnerModeLevelEnd, (data) => {
            this.socketActionController.onBeginnerModeLevelEnd()
        })

        this.gameStateHandlers.set(GameUpdate.Vote, (data: VoteState) => {
            const voteState = data.done ? undefined : data
            this.uiGameManager?.gameStore.getState().gameVote.setVoteState(voteState)
        })

        this.gameStateHandlers.set(GameUpdate.SelectPlayer, (data: SelectPlayerFormat) => {
            this.socketActionController.onSelectPlayer(data)
        })

        this.gameStateHandlers.set(GameUpdate.ExchangeCards, (data: CardExchangeFormat) => {
            this.socketActionController.onExchangeCards(data)
        })

        this.gameStateHandlers.set(GameUpdate.HighlightCorners, (data: number[]) => {
            this.socketActionController.onHighlightCorners(data)
        })

        this.gameStateHandlers.set(GameUpdate.HighlightRoadEdges, (data: number[]) => {
            this.socketActionController.onHighlightRoadEdges(data)
        })

        this.gameStateHandlers.set(GameUpdate.HighlightShipEdges, (data: number[]) => {
            this.socketActionController.onHighlightShipEdges(data)
        })

        this.gameStateHandlers.set(GameUpdate.HighlightTiles, (data: number[]) => {
            this.socketActionController.onHighlightTiles(data)
        })

        this.gameStateHandlers.set(GameUpdate.HighlightDiceNumbersForInventor, (data: number[]) => {
            this.socketActionController.onHighlightDiceNumbersForInventor(data)
        })

        this.gameStateHandlers.set(GameUpdate.AmountOfCardsToDiscard, (data: SelectCardToDiscardFormat) => {
            this.socketActionController.onAmountOfCardsToDiscard(data)
            Sound.playSound(Sound.SoundType.DiscardNotification)
        })

        this.gameStateHandlers.set(GameUpdate.DiscardBroadcast, () => {
            Sound.playSound(Sound.SoundType.DiscardBroadcast)
        })

        this.gameStateHandlers.set(GameUpdate.SelectCardsToGiveToPlayer, (data: SelectCardsToGiveToAnotherPlayer) => {
            this.socketActionController.onSelectCardsToGiveToPlayer(data)
            Sound.playSound(Sound.SoundType.YourTurn)
        })

        this.gameStateHandlers.set(GameUpdate.CardsToSelectForDevelopmentCard, (data: SelectCardPopupUsingDevelopmentCardFormat) => {
            this.socketActionController.onCardsToSelectForDevelopmentCard(data)
        })

        this.gameStateHandlers.set(GameUpdate.SelectPlayerAndCard, (data: SelectPlayerAndResourceCardFormat) => {
            this.socketActionController.onSelectPlayerAndCard(data)
        })

        this.gameStateHandlers.set(GameUpdate.CardsToSelectForPendingTransaction, (data: SelectCardPopupForPendingDistributionFormat) => {
            this.socketActionController.onCardsToSelectForPendingTransaction(data)
        })

        this.gameStateHandlers.set(GameUpdate.ConfirmUseDevelopmentCard, (data: CardPopupInformation) => {
            this.socketActionController.onConfirmUseDevelopmentCard(data)
        })

        this.gameStateHandlers.set(GameUpdate.SelectPlayerUsingDevelopmentCard, (data: SelectPlayerUsingDevelopmentCardFormat) => {
            this.socketActionController.onSelectPlayerUsingDevelopmentCard(data)
        })

        this.gameStateHandlers.set(GameUpdate.StealDevelopmentCardFromPlayer, (data: SelectCardPopupStealCardFromAnotherPlayerFormat) => {
            this.socketActionController.onStealDevelopmentCardFromPlayer(data)
        })

        this.gameStateHandlers.set(GameUpdate.StealResourceCardFromPlayer, (data: SelectCardPopupStealCardFromAnotherPlayerFormat) => {
            this.socketActionController.onStealResourceCardFromPlayer(data)
        })

        this.gameStateHandlers.set(GameUpdate.ClosePopupUI, () => {
            this.socketActionController.onClosePopupUI()
        })

        this.gameStateHandlers.set(GameUpdate.SelectDice, (data: SelectDiceFormat) => {
            this.socketActionController.onSelectDice(data)
        })

        this.gameStateHandlers.set(GameUpdate.CityImprovementStateUpdated, () => {
            this.socketActionController.onCityImprovementStateUpdated()
        })

        this.gameStateHandlers.set(GameUpdate.ForceShowCityImprovementButtons, () => {
            this.socketActionController.onForceShowCityImprovementButtons()
        })

        this.gameStateHandlers.set(GameUpdate.PlayTurnSound, () => {
            Sound.playSound(Sound.SoundType.YourTurn)
        })

        this.gameStateHandlers.set(GameUpdate.InfoMessage, (data: TranslationObject) => {
            const popup = new ViewPopupTemporary({key: 'strings:popups.informationView.title'}, data)
        })

        this.gameStateHandlers.set(GameUpdate.ResetTradeStateAtEndOfTurn, (data: undefined) => {
            this.socketActionController.onResetTradeStateAtEndOfTurn()
        })

        this.gameStateHandlers.set(GameUpdate.BoldTextInTradeWindow, (cardsToBold: CardEnum[]) => {
            this.socketActionController.onBoldTextInTradeWindow(cardsToBold)
        })

        this.gameStateHandlers.set(GameUpdate.BuildGame, (data: BuildGameConfigurations) => {
            this.socketActionController.onBuildGame(data)
            const modeEventLabel = 'Mode-' + ModeSettingEnum[data.gameSettings.modeSetting]
            const mapEventLabel = 'Map-' + MapSettingEnum[data.gameSettings.mapSetting]
            CA.sendGoogleAnalyticsEventOnce(CA.Category.Game, modeEventLabel)
            CA.sendGoogleAnalyticsEventOnce(CA.Category.Game, mapEventLabel)

            if(!this.playedStartGameSound) {
                SocketGameListen.playGameStartedSound(data.gameSettings.modeSetting)
                CA.sendGoogleAnalyticsEvent(CA.Category.Funnel, CA.Label.FunnelStartedGame)
                this.playedStartGameSound = true
            }
        })

        this.gameStateHandlers.set(GameUpdate.GameStateUpdated, (timedGameStateUpdate: SocketGameStateUpdate) => {
            this.socketActionController.gameStateUpdated(timedGameStateUpdate, true)
        })

        this.gameStateHandlers.set(GameUpdate.GivePlayerResourcesFromTile, (data: BankTransactionLedger[]) => {
            if(data.length !== 0) CA.sendGoogleAnalyticsEventOnce(CA.Category.Funnel, CA.Label.FunnelGameGetResources)
            this.socketActionController.onGivePlayerResourcesFromTile(data)
        })

        this.gameStateHandlers.set(GameUpdate.RematchLink, (data) => {
            window.location.hash = data
            window.location.reload()
        })

        this.gameStateHandlers.set(GameUpdate.EndGameText, (data) => {
            this.socketActionController.onEndGameText(data)
        })

        this.gameStateHandlers.set(GameUpdate.TogglePauseGame, (data) => {
            this.socketActionController.onTogglePauseGame(data)
        })

        this.gameStateHandlers.set(GameUpdate.CanResignGame, (canResign: boolean) => {
            this.gameState.canResign = canResign
            setGlobalCanResign(canResign)
        })

        this.gameStateHandlers.set(GameUpdate.GameEndState, (gameEndData: GameEndData) => {
            this.uiGameManager.socketGameSend.endBeginnerModeLevel()
            socketActionController.onGameEndState(gameEndData)
            socketController.reconnectionController.disable()
            this.gameClientRestorationController.disable()

            CA.sendGoogleTagManagerEvent(CA.GoogleTagManagerEvent.GameFinished)
        })

        this.gameStateHandlers.set(GameUpdate.ExitInitialPlacement, () => {
            SocketGameListen.playSettlementPhaseEndedSound(this.uiGameManager.gameController.gameSettings.modeSetting)
            setTimeout(() => {
                this.uiGameManager.eventController.sendEvent(undefined, GameStateEnums.UIGameEventEnums.EndOfInitialPlacement)
            }, GAME_STARTED_DELAY_FOR_NATURAL_EFFECT)
        })

        this.gameStateHandlers.set(GameUpdate.TradeFinalized, () => {
            Sound.playSound(Sound.SoundType.OfferAccepted)
        })

        this.gameStateHandlers.set(GameUpdate.TradeRejectedByAll, () => {
            Sound.playSound(Sound.SoundType.OfferRejected)
        })

        this.gameStateHandlers.set(GameUpdate.KnightPlaced, () => {
            Sound.playSound(Sound.SoundType.KnightPlace)
        })

        this.gameStateHandlers.set(GameUpdate.KarmaState, (data: boolean) => {
            this.uiGameManager.gameController.gameSettings.karmaActive = data
            this.uiGameManager.gameStore.getState().gameSettings.setIsKarmaActive(data)
        })

        this.gameStateHandlers.set(GameUpdate.KnightActivated, () => {
            Sound.playSound(Sound.SoundType.KnightEquip)
        })

        this.gameStateHandlers.set(GameUpdate.KnightUpgraded, () => {
            Sound.playSound(Sound.SoundType.KnightUpgrade)
        })

        this.gameStateHandlers.set(GameUpdate.PlayerReconnected, () => {
            Sound.playSound(Sound.SoundType.Reconnect)
        })

        this.gameStateHandlers.set(GameUpdate.PlayerDisconnected, () => {
            Sound.playSound(Sound.SoundType.Disconnect)
        })

        this.gameStateHandlers.set(GameUpdate.SpectatorChatMessage, (data: GameChatMessageData) => {
            addGlobalSpectatorMessage(data)
        })

        this.gameStateHandlers.set(GameUpdate.ResetBeginnerModeLevel, () => {
            StorageWrapper.resetBeginnerModeLevel()
        })

        this.gameStateHandlers.set(GameUpdate.SendBroadcast, (data: ChatData) => {
            notificationController.show(data.text, UITopNotificationType.Information)
        })

        this.gameStateHandlers.set(GameUpdate.Ping, () => {
            this.socketActionController.uiGameManager?.socketGameSend.pong()
        })

        this.gameStateHandlers.set(GameUpdate.TriggerClientGameStateReset, () => {
            this.restoreGameClientWithoutClosingSocket()
        })
    }

    private restoreGameClientWithoutClosingSocket() {
        this.uiGameManager?.eventController.sendEvent(undefined, GameStateEnums.UIGameEventEnums.ClientReconnectionTriggered)
        this.reset()
        this.gameClientRestorationController.startReconnecting()
    }

    private reset() {
        this.gameStateReceiver?.reset()
    }

    private static playGameStartedSound(mode: ModeSettingEnum) {
        const getGameStartedSoundForMode = (modeSetting: ModeSettingEnum) => {
            switch(modeSetting) {
                case ModeSettingEnum.ColonistRush4P:
                    return Sound.SoundType.GameStartedGuitar
                default:
                    return Sound.SoundType.GameStarted
            }
        }

        const gameStartedSound = getGameStartedSoundForMode(mode)
        setTimeout(() => Sound.playSound(gameStartedSound), GAME_STARTED_DELAY_FOR_NATURAL_EFFECT)
    }

    private static playSettlementPhaseEndedSound(mode: ModeSettingEnum) {
        const getSettlementPhaseEndedSoundForMode = (modeSetting: ModeSettingEnum) => {
            switch(modeSetting) {
                case ModeSettingEnum.ColonistRush4P:
                    return Sound.SoundType.SettlementPhaseEndedGuitar
                default:
                    return Sound.SoundType.SettlementPhaseEnded
            }
        }

        const settlementPhaseEndedSound = getSettlementPhaseEndedSoundForMode(mode)
        setTimeout(() => Sound.playSound(settlementPhaseEndedSound), GAME_STARTED_DELAY_FOR_NATURAL_EFFECT)
    }
}
